import type {
  AppendToStreamResult,
  Command,
  Event,
  EventStore,
  ExpectedStreamVersion,
  ReadEvent,
  StreamId,
  StreamPosition,
} from '../core/types';
import { isStreamVersionConflictError } from '../errors';

// #region Core Types

/**
 * Result of command handling operation
 */
export type CommandHandlerResult<State, TEvent extends Event = Event> = {
  newState: State;
  newEvents: TEvent[];
  appendResult: AppendToStreamResult;
};

/**
 * Handler function that processes state and returns events
 */
export type Handler<State, TEvent extends Event = Event> = (
  state: State
) => TEvent | TEvent[] | Promise<TEvent | TEvent[]>;

/**
 * Decider pattern for command handling
 */
export type Decider<State, TCommand extends Command, TEvent extends Event> = {
  decide: (command: TCommand, state: State) => TEvent | TEvent[];
  evolve: (state: State, event: ReadEvent<TEvent>) => State;
  initialState: () => State;
};

// #endregion

// #region Retry Logic

export type AsyncRetryOptions = {
  retries: number;
  minTimeout: number;
  factor: number;
  shouldRetryError?: (error: Error) => boolean;
};

export const CommandHandlerStreamVersionConflictRetryOptions: AsyncRetryOptions =
  {
    retries: 3,
    minTimeout: 100,
    factor: 1.5,
    shouldRetryError: isStreamVersionConflictError,
  };

/**
 * Async retry function with exponential backoff
 */
async function retry<T>(
  fn: () => Promise<T>,
  options: AsyncRetryOptions
): Promise<T> {
  let lastError: Error | undefined;

  for (let attempt = 0; attempt <= options.retries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));

      // Don't retry if we've exhausted attempts or error shouldn't be retried
      if (
        attempt === options.retries ||
        (options.shouldRetryError && !options.shouldRetryError(lastError))
      ) {
        throw lastError;
      }

      // Calculate delay with exponential backoff
      const delay = options.minTimeout * options.factor ** attempt;
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  throw lastError ?? new Error('Retry failed with unknown error');
}

// #endregion

// #region Command Handler Options

export type CommandHandlerOptions<State, TEvent extends Event> = {
  evolve: (state: State, event: ReadEvent<TEvent>) => State;
  initialState: () => State;
  expectedStreamVersion?: ExpectedStreamVersion<StreamPosition>;
  retryOptions?: AsyncRetryOptions;
};

export type DeciderCommandHandlerOptions = {
  expectedStreamVersion?: ExpectedStreamVersion<StreamPosition>;
  retryOptions?: AsyncRetryOptions;
};

// #endregion

// #region Main Command Handlers

/**
 * Creates a command handler without Decider pattern
 *
 * Steps:
 * 1. Aggregate the stream to get current state
 * 2. Run business logic using Handler function
 * 3. Append events to stream
 * 4. Return result with new state and events
 *
 * @param eventStore - The event store instance
 * @param streamId - The stream identifier
 * @param handler - The handler function that processes state and returns events
 * @param options - Command handler options including evolve, initialState, etc.
 * @returns Promise resolving to command handler result with new state and events
 * @public
 * @example
 * ```typescript
 * const result = await handleCommand(
 *   eventStore,
 *   'user-123',
 *   (state) => [{ type: 'UserRegistered', data: { userId: '123' } }],
 *   {
 *     evolve: (state, event) => ({ ...state, registered: true }),
 *     initialState: () => ({ registered: false })
 *   }
 * );
 * ```
 */
export async function handleCommand<State, TEvent extends Event = Event>(
  eventStore: EventStore,
  streamId: StreamId,
  handler: Handler<State, TEvent>,
  options: CommandHandlerOptions<State, TEvent>
): Promise<CommandHandlerResult<State, TEvent>> {
  const operation = async (): Promise<CommandHandlerResult<State, TEvent>> => {
    const { evolve, initialState, expectedStreamVersion } = options;

    // Step 1: Aggregate the stream to get current state
    const aggregationResult = await eventStore.aggregateStream<State, TEvent>(
      streamId,
      {
        evolve,
        initialState,
      }
    );

    const {
      state: currentState,
      currentStreamVersion,
      streamExists,
    } = aggregationResult;

    // Step 2: Run business logic using Handler function
    const handlerResult = await handler(currentState);
    const newEvents = Array.isArray(handlerResult)
      ? handlerResult
      : [handlerResult];

    // If no events to append, return current state
    if (newEvents.length === 0) {
      return {
        newState: currentState,
        newEvents: [],
        appendResult: {
          nextExpectedStreamVersion: currentStreamVersion,
          createdNewStream: false,
        },
      };
    }

    // Calculate new state by applying new events
    let newState = currentState;
    for (let i = 0; i < newEvents.length; i++) {
      const event = newEvents[i];
      if (!event) continue;

      // Create ReadEvent for evolve function
      const readEvent = {
        type: event.type,
        data: event.data,
        metadata: event.metadata,
        streamId,
        streamPosition: currentStreamVersion + i + 1,
        globalPosition: 0, // Will be set by event store
        eventId: '', // Will be set by event store
        schemaVersion: '1.0.0',
        transactionId: '',
        createdAt: new Date().toISOString(),
      } as ReadEvent<TEvent>;
      newState = evolve(newState, readEvent);
    }

    // Determine expected stream version
    const finalExpectedStreamVersion =
      expectedStreamVersion ??
      (streamExists ? currentStreamVersion : 'no_stream');

    // Step 3: Append events to stream
    const appendResult = await eventStore.appendToStream(streamId, newEvents, {
      expectedStreamVersion: finalExpectedStreamVersion,
    });

    // Step 4: Return result
    return {
      newState,
      newEvents,
      appendResult,
    };
  };

  // Apply retry logic if configured
  if (options.retryOptions) {
    return retry(operation, options.retryOptions);
  }

  return operation();
}

/**
 * Creates a command handler with Decider pattern
 *
 * Steps:
 * 1. Aggregate the stream to get current state
 * 2. Run business logic using Decider.decide function
 * 3. Append events to stream
 * 4. Return result with new state and events
 *
 * @param eventStore - The event store instance
 * @param streamId - The stream identifier
 * @param command - The command to handle
 * @param decider - The decider implementation with decide, evolve, and initialState
 * @param options - Optional command handler options
 * @returns Promise resolving to command handler result with new state and events
 * @public
 * @example
 * ```typescript
 * const result = await handleCommandWithDecider(
 *   eventStore,
 *   'user-123',
 *   { type: 'RegisterUser', data: { userId: '123' } },
 *   {
 *     decide: (state, cmd) => [{ type: 'UserRegistered', data: cmd.data }],
 *     evolve: (state, event) => ({ ...state, registered: true }),
 *     initialState: () => ({ registered: false })
 *   }
 * );
 * ```
 */
export async function handleCommandWithDecider<
  State,
  TCommand extends Command,
  TEvent extends Event = Event,
>(
  eventStore: EventStore,
  streamId: StreamId,
  command: TCommand,
  decider: Decider<State, TCommand, TEvent>,
  options?: DeciderCommandHandlerOptions
): Promise<CommandHandlerResult<State, TEvent>> {
  const operation = async (): Promise<CommandHandlerResult<State, TEvent>> => {
    const { expectedStreamVersion } = options || {};
    const { evolve, initialState, decide } = decider;

    // Step 1: Aggregate the stream to get current state
    const aggregationResult = await eventStore.aggregateStream<State, TEvent>(
      streamId,
      {
        evolve,
        initialState,
      }
    );

    const {
      state: currentState,
      currentStreamVersion,
      streamExists,
    } = aggregationResult;

    // Step 2: Run business logic using Decider.decide function
    const decideResult = decide(command, currentState);
    const newEvents = Array.isArray(decideResult)
      ? decideResult
      : [decideResult];

    // If no events to append, return current state
    if (newEvents.length === 0) {
      return {
        newState: currentState,
        newEvents: [],
        appendResult: {
          nextExpectedStreamVersion: currentStreamVersion,
          createdNewStream: false,
        },
      };
    }

    // Calculate new state by applying new events
    let newState = currentState;
    for (let i = 0; i < newEvents.length; i++) {
      const event = newEvents[i];
      if (!event) continue;

      // Create ReadEvent for evolve function
      const readEvent = {
        type: event.type,
        data: event.data,
        metadata: event.metadata,
        streamId,
        streamPosition: currentStreamVersion + i + 1,
        globalPosition: 0, // Will be set by event store
        eventId: '', // Will be set by event store
        schemaVersion: '1.0.0',
        transactionId: '',
        createdAt: new Date().toISOString(),
      } as ReadEvent<TEvent>;
      newState = evolve(newState, readEvent);
    }

    // Determine expected stream version
    const finalExpectedStreamVersion =
      expectedStreamVersion ??
      (streamExists ? currentStreamVersion : 'no_stream');

    // Step 3: Append events to stream
    const appendResult = await eventStore.appendToStream(streamId, newEvents, {
      expectedStreamVersion: finalExpectedStreamVersion,
    });

    // Step 4: Return result
    return {
      newState,
      newEvents,
      appendResult,
    };
  };

  // Apply retry logic if configured
  if (options?.retryOptions) {
    return retry(operation, options.retryOptions);
  }

  return operation();
}

// #endregion

// #region Convenience Functions

/**
 * Handle command with automatic retry on version conflicts
 */
export function handleCommandWithRetry<State, TEvent extends Event = Event>(
  eventStore: EventStore,
  streamId: StreamId,
  handler: Handler<State, TEvent>,
  options: CommandHandlerOptions<State, TEvent>
): Promise<CommandHandlerResult<State, TEvent>> {
  return handleCommand(eventStore, streamId, handler, {
    ...options,
    retryOptions:
      options.retryOptions || CommandHandlerStreamVersionConflictRetryOptions,
  });
}

/**
 * Handle command with Decider and automatic retry on version conflicts
 */
export function handleCommandWithDeciderAndRetry<
  State,
  TCommand extends Command,
  TEvent extends Event = Event,
>(
  eventStore: EventStore,
  streamId: StreamId,
  command: TCommand,
  decider: Decider<State, TCommand, TEvent>,
  options?: DeciderCommandHandlerOptions
): Promise<CommandHandlerResult<State, TEvent>> {
  return handleCommandWithDecider(eventStore, streamId, command, decider, {
    ...options,
    retryOptions:
      options?.retryOptions || CommandHandlerStreamVersionConflictRetryOptions,
  });
}

// #endregion
